// utils/filters/MilestoneCurrentFilter.js
import React from 'react';
import { Input, Checkbox, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import DateFilterComponent from './DateFilterComponent';
import BinnedDateFilterComponent from './complianceCheck';

// Cache for expensive computations
const memoCache = new Map();

export const handleMilestoneCurrentFilter = (
  milestoneCurrentFilter,
  initiatives,
  attributeFilters,
  setAttributeFilters,
  searchTerm,
  setSearchTerm,
  users
) => {
  const updateAttributeFilters = (newValue) => {
    setAttributeFilters({
      ...attributeFilters,
      [milestoneCurrentFilter]: newValue,
    });
  };

  const handleClearFilters = () => {
    setAttributeFilters({});
    setSearchTerm('');
  };

  const clearButton = Object.keys(attributeFilters).length > 0 && (
    <Button
      icon={<CloseOutlined />}
      onClick={handleClearFilters}
      style={{ marginBottom: '10px', color: 'blue' }}
    >
      Clear Filters
    </Button>
  );

  // Helper function to render checkbox options with limit and search
  const renderCheckboxOptions = (options, currentFilterKey) => {
    // Convert options to strings once
    const stringOptions = options.map(String);
    
    // Filter options based on search term
    let filteredOptions = stringOptions;
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filteredOptions = stringOptions.filter((item) =>
        item.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Determine display options with limit
    let displayOptions = filteredOptions;
    let hasMoreOptions = false;
    
    if (!searchTerm && filteredOptions.length > 60) {
      displayOptions = filteredOptions.slice(0, 60);
      hasMoreOptions = true;
    }

    // Select all state
    const currentFilterValue = attributeFilters[currentFilterKey];
    const allSelected = currentFilterValue === undefined ||
      (currentFilterValue && currentFilterValue.length === stringOptions.length);
    
    const isIndeterminate = currentFilterValue && 
      currentFilterValue.length > 0 && 
      currentFilterValue.length < stringOptions.length;

    const handleSearchChange = (e) => {
      setSearchTerm(e.target.value);
    };

    const handleSelectAllChange = (e) => {
      updateAttributeFilters(e.target.checked ? stringOptions : []);
    };

    const handleCheckboxChange = (item, checked) => {
      const currentFilters = attributeFilters[currentFilterKey] || [];
      updateAttributeFilters(
        checked
          ? [...currentFilters, item]
          : currentFilters.filter((val) => val !== item)
      );
    };

    return React.createElement('div', { style: { marginTop: '10px' } },
      clearButton,
      React.createElement(Input, {
        placeholder: "Search",
        onChange: handleSearchChange,
        style: { marginBottom: '10px' },
        value: searchTerm,
        allowClear: true
      }),
      stringOptions.length > 0 && React.createElement(Checkbox, {
        indeterminate: isIndeterminate,
        checked: allSelected,
        onChange: handleSelectAllChange
      }, `(Select all - ${stringOptions.length} items)`),
      React.createElement('div', { style: { maxHeight: '400px', overflowY: 'auto', marginTop: '8px' } },
        displayOptions.map((item) => {
          const isChecked = attributeFilters[currentFilterKey] === undefined ||
            attributeFilters[currentFilterKey]?.includes(item);
          
          return React.createElement('div', { key: item, style: { marginBottom: '4px' } },
            React.createElement(Checkbox, {
              checked: isChecked,
              onChange: (e) => handleCheckboxChange(item, e.target.checked)
            }, React.createElement('span', { style: { fontSize: '13px' } }, item))
          );
        })
      ),
      hasMoreOptions && React.createElement('div', { style: { padding: '8px 0', color: '#888', fontSize: '12px' } },
        `Displaying first 60 results. Please use search to find more (${filteredOptions.length - 60} remaining).`
      ),
      searchTerm !== '' && filteredOptions.length === 0 && React.createElement('div', { style: { padding: '8px 0', color: '#888', fontSize: '12px' } },
        `No results found for "${searchTerm}".`
      )
    );
  };

  // Extract all milestones once with caching
  const getMilestoneData = () => {
    const cacheKey = `milestones_${initiatives.length}`;
    if (memoCache.has(cacheKey)) {
      return memoCache.get(cacheKey);
    }
    
    const allMilestones = initiatives.flatMap(i => i.milestones || []);
    const result = { allMilestones };
    memoCache.set(cacheKey, result);
    
    // Clear cache if it gets too large
    if (memoCache.size > 50) {
      const firstKey = memoCache.keys().next().value;
      memoCache.delete(firstKey);
    }
    
    return result;
  };

  const { allMilestones } = getMilestoneData();

  // Create user map for O(1) lookups
  const getUserMap = () => {
    const cacheKey = `users_${users.length}`;
    if (memoCache.has(cacheKey)) {
      return memoCache.get(cacheKey);
    }
    
    const map = new Map();
    users.forEach(user => map.set(user._id, user));
    memoCache.set(cacheKey, map);
    return map;
  };

  const userMap = getUserMap();

  // Owner filter
  if (milestoneCurrentFilter === 'Owner') {
    const uniqueOwners = [...new Set(allMilestones.map((imp) => imp.Owner).filter(Boolean))];
    const ownerNames = uniqueOwners
      .map((ownerId) => {
        const user = userMap.get(ownerId);
        return user ? `${user.firstName} ${user.lastName}` : ownerId;
      })
      .sort();
    
    return renderCheckboxOptions(ownerNames, milestoneCurrentFilter);
  }

  // Date Filters
  if (['Work starting date', 'Milestone completion date', 'Created On', 'Updated On'].includes(milestoneCurrentFilter)) {
    return React.createElement(React.Fragment, null,
      clearButton,
      React.createElement(DateFilterComponent, {
        currentFilter: milestoneCurrentFilter,
        attributeFilters: attributeFilters,
        setAttributeFilters: setAttributeFilters
      })
    );
  }

  // Binned Date Filter
  if (milestoneCurrentFilter === 'Change in Milestone completion date (binned, current vs last week)') {
    return React.createElement(React.Fragment, null,
      clearButton,
      React.createElement(BinnedDateFilterComponent, {
        currentFilter: milestoneCurrentFilter,
        attributeFilters: attributeFilters,
        setAttributeFilters: setAttributeFilters
      })
    );
  }

  // Created by / Updated by filters
  if (milestoneCurrentFilter === 'Created by' || milestoneCurrentFilter === 'Updated by') {
    const names = [];
    
    for (const initiative of initiatives) {
      const milestones = initiative.milestones || [];
      
      for (const milestone of milestones) {
        let matchingEntries = [];
        
        if (milestoneCurrentFilter === 'Created by') {
          matchingEntries = (initiative.history || [])
            .filter((h) => h.action?.includes(`Milestone #${milestone.name} created`))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        } else if (milestoneCurrentFilter === 'Updated by') {
          matchingEntries = (initiative.history || [])
            .filter((h) =>
              h.action?.includes(`Milestone with Number ${milestone.milestoneNumber} has been updated.`)
            )
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        }
        
        const userId = matchingEntries[0]?.user;
        const user = userMap.get(userId);
        names.push(user ? `${user.firstName} ${user.lastName}` : '<not set>');
      }
    }
    
    const milestoneUserNames = [...new Set(names)].sort();
    return renderCheckboxOptions(milestoneUserNames, milestoneCurrentFilter);
  }

  // milestoneNumber - name filter
  if (milestoneCurrentFilter === 'milestoneNumber - name') {
    const uniqueValues = [...new Set(
      allMilestones.map((imp) => `${imp.milestoneNumber} - ${imp.name}`)
    )].sort();
    
    return renderCheckboxOptions(uniqueValues, milestoneCurrentFilter);
  }

  // name or milestoneNumber filters
  if (milestoneCurrentFilter === 'name' || milestoneCurrentFilter === 'milestoneNumber') {
    let uniqueValues = [...new Set(
      allMilestones.map((imp) => imp[milestoneCurrentFilter])
    )];
    
    if (milestoneCurrentFilter === 'milestoneNumber') {
      uniqueValues = uniqueValues.sort((a, b) => Number(a) - Number(b));
    } else {
      uniqueValues = uniqueValues.sort((a, b) => String(a).localeCompare(String(b)));
    }
    
    return renderCheckboxOptions(uniqueValues, milestoneCurrentFilter);
  }

  // Planning summary (simplified) - Static options
  if (milestoneCurrentFilter === 'Planning summary (simplified)') {
    const planningSummarySimplifiedOptions = [
      '<not set>', 'Not planned', 'Late', 'Late to start', 'Lately Started', 'Lately Completed', 'Not started',
      'Due soon', 'Due soon for 30 days', 'On plan', 'Completed', 'Cancelled', 'Initiative cancelled',
      'Initiative on hold',
    ].sort();
    
    return renderCheckboxOptions(planningSummarySimplifiedOptions, milestoneCurrentFilter);
  }

  // Milestone Status - Static options
  if (milestoneCurrentFilter === 'Milestone Status') {
    const status = [
      'Not Started', 'Started', 'Completed', 'Cancelled'
    ].sort();
    
    return renderCheckboxOptions(status, milestoneCurrentFilter);
  }

  // Milestone Planning summary - Static options
  if (
    milestoneCurrentFilter === 'Milestone Planning summary' ||
    milestoneCurrentFilter === '(LW) Planning summary'
  ) {
    const planningSummaryOptions = [
      '<not set>', 'Not planned', 'Late (more than 4 weeks)', 'Late (1 to 4 weeks)',
      'Late (less than 1 week)', 'Late to start', 'Lately Completed (more than 4 weeks)', 'Lately Completed (1 to 4 weeks)',
      'Lately Completed (less than 1 week)', 'Late to start (1 to 4 weeks)', 'Late to start (less than 1 week)', 'Late to start (more than 4 weeks)',
      'Lately Started (less than 1 week)', 'Lately Started (1 to 4 weeks)', 'Lately Started (more than 4 weeks)', 'Not started', 'Due soon', 'Due soon for 30 days',
      'On plan', 'Completed', 'Cancelled', 'Initiative on hold', 'Initiative cancelled',
    ].sort();
    
    return renderCheckboxOptions(planningSummaryOptions, milestoneCurrentFilter);
  }

  // Default case
  const uniqueValues = [...new Set(
    allMilestones.map((imp) => imp[milestoneCurrentFilter])
  )].filter(Boolean).sort();
  
  return renderCheckboxOptions(uniqueValues, milestoneCurrentFilter);
};